// Options for a static field's "Field mapping".
//
// Each value is a property on the inspection object (see `InspectionItemType`
// in modules/clientModule/types.ts) that `clearTemplateTexts` (docOvelayTool.ts)
// reads via `selectedInspection[value]` when the report is generated. The
// special value 'NOW' inserts today's date instead of an inspection property.
// Labels mirror what the app already shows for these fields (InspectionEditor).
//
// These are the builder's ORIGINAL, hand-picked mappings, kept so existing
// forms keep printing. Every Regular Forms (Form Setup) field is offered as
// well, as a `form:` mapping — see "Shared with Regular Forms" below. New
// mappings belong in the backend's form_setup/form_fields.py, not here.
//
// The options are grouped by WHERE the value comes from, because a bare
// alphabetical list of 25 entries gave no clue what "Field mapping" was for.
// The group name is the record the value is read off — pick the group first,
// then the field on it.
export interface StaticFieldOption {
  value: string;
  label: string;
  /** Plain-English note on what actually prints. Shown under the select. */
  hint?: string;
}

export interface StaticFieldGroup {
  group: string;
  options: StaticFieldOption[];
}

// The company name, which is not stored anywhere: the four Franchise records
// are branches ('Naples', 'Sarasota', 'Fort Myers', 'Ocala'), and nothing above
// them holds the company. Hard-coded here so a form can print it at all.
export const COMPANY_NAME = 'Elite Permits';

export const STATIC_FIELD_GROUPS: StaticFieldGroup[] = [
  {
    group: 'Company',
    options: [
      {
        value: 'elitePermitsName',
        label: 'Elite Permits Name',
        hint:
          "Always prints \"Elite Permits\". A fixed value, not read from the inspection — unlike Contact > Company Name, which is the contact's company.",
      },
    ],
  },
  {
    group: 'Date',
    options: [
      {
        value: 'NOW',
        label: "Today's Date",
        hint:
          'The date the report is first filled in — it is stamped once and then stays put, so an old report is never silently re-dated.',
      },
    ],
  },
  {
    group: 'Inspection',
    options: [
      { value: 'id', label: 'Inspection Number' },
      { value: 'inspectionDate', label: 'Inspection Date' },
      { value: 'activityTypeName', label: 'Inspection Type' },
      { value: 'activityTitleName', label: 'Activity Title' },
      { value: 'statusName', label: 'Status' },
      { value: 'followUp', label: 'Follow-Up Date' },
      { value: 'completedAt', label: 'Completed Date' },
      {
        value: 'information',
        label: 'Information / Notes',
        hint:
          'Free text, often long — switch on "Wrap long text onto multiple lines" for this one.',
      },
    ],
  },
  {
    group: 'Inspector',
    options: [
      {
        value: 'inspectorFullName',
        label: 'Inspector First and Last Name',
        hint:
          'Both names in one field. Drops the space if either half is missing, so a half-filled record never prints a stray gap.',
      },
      { value: 'inspectorFirstName', label: 'Inspector First Name' },
      { value: 'inspectorLastName', label: 'Inspector Last Name' },
      {
        value: 'inspectorLicense',
        label: 'Inspector License',
        hint:
          'The License(s) on the assigned inspector\'s user record — the same person the name fields above print. Set it under Users > (inspector) > License(s); the field only appears there for the Inspector role. Blank on the report means blank on that record.',
      },
    ],
  },
  {
    group: 'Project',
    options: [
      { value: 'projectName', label: 'Project Name' },
      { value: 'projectAddress', label: 'Address' },
      { value: 'projectJurisdiction', label: 'Jurisdiction' },
      { value: 'gateCode', label: 'Gate Code' },
    ],
  },
  {
    group: 'Permit',
    options: [
      { value: 'permitName', label: 'Permit Name' },
      { value: 'permitNumber', label: 'Permit Number' },
      { value: 'permitStatus', label: 'Permit Status' },
    ],
  },
  {
    group: 'Project Manager',
    options: [
      {
        value: 'managerFirstName',
        label: 'Manager First Name',
        hint: "The permit's assigned manager, not the inspector.",
      },
      { value: 'managerLastName', label: 'Manager Last Name' },
    ],
  },
  {
    group: 'Contact',
    options: [
      { value: 'contactFirstName', label: 'Contact First Name' },
      { value: 'contactLastName', label: 'Contact Last Name' },
      { value: 'companyName', label: 'Company Name' },
      { value: 'phoneNumber', label: 'Phone Number' },
      { value: 'cellPhone', label: 'Cell Phone' },
    ],
  },
];

// Flat view of the same options. Kept so callers that only need "is this a
// known mapping" (the "(custom)" fallback in FieldEditorCard) stay simple, and
// so the two lists can never disagree about which values exist.
export const STATIC_FIELD_OPTIONS: StaticFieldOption[] = STATIC_FIELD_GROUPS.reduce(
  (all: StaticFieldOption[], g) => all.concat(g.options),
  [],
);

// Mappings that are BUILT from the inspection rather than read straight off it.
//
// clearTemplateTexts otherwise does a plain `selectedInspection[value]` lookup,
// which cannot express "first name, a space, then last name". Deriving here —
// instead of adding a combined field to the API — keeps this working against
// whichever serializer fed the editor and needs no deploy.
//
// Always join with filter(Boolean): the API returns null for an unset name, and
// a template literal would print "John null" or leave a trailing space.
export const DERIVED_STATIC_FIELDS: Record<
  string,
  (inspection: Record<string, any>) => string
> = {
  inspectorFullName: i =>
    [i.inspectorFirstName, i.inspectorLastName].filter(Boolean).join(' '),

  // Ignores the inspection entirely — the value is the same on every report.
  // Living here rather than in resolveStaticValue means no other module has to
  // learn about it: the existing lookup already prefers a derived mapping.
  elitePermitsName: () => COMPANY_NAME,
};

// Marks a mapping shared with Regular Forms — see "Shared with Regular Forms"
// at the end of this file.
export const FORM_FIELD_PREFIX = 'form:';

export const isFormFieldMapping = (value?: string): boolean =>
  typeof value === 'string' && value.startsWith(FORM_FIELD_PREFIX);

/**
 * The text a static field should print for an inspection. Handles the derived
 * mappings above and falls back to a direct property read for the rest.
 * 'NOW' is NOT handled here — it is a date, resolved by the caller.
 */
export const resolveStaticValue = (
  inspection: Record<string, any>,
  value: string,
) => {
  const source = inspection || {};
  const derive = DERIVED_STATIC_FIELDS[value];
  let resolved;
  if (isFormFieldMapping(value)) {
    // Resolved by the server (see formFieldValues below), never read off the
    // inspection directly — `source['form:...']` is always undefined.
    resolved = source.formFieldValues?.[value];
  } else {
    resolved = derive ? derive(source) : source[value];
  }
  // The API returns null for anything unset — a permit with no number, an
  // inspection with no follow-up date. Returning that raw put the literal text
  // "null" on issued reports once it was interpolated. An absent value prints
  // as nothing; 0 and false are real answers and are preserved.
  return resolved === null || resolved === undefined ? '' : resolved;
};

/** The group a mapping belongs to, e.g. 'Inspector' — undefined if custom. */
export const staticFieldGroupOf = (value: string): string | undefined =>
  STATIC_FIELD_GROUPS.find(g => g.options.some(o => o.value === value))?.group;

/** The full option behind a saved value, or undefined when it is custom. */
export const staticFieldOption = (
  value: string,
): StaticFieldOption | undefined =>
  STATIC_FIELD_OPTIONS.find(o => o.value === value);

// ---------------------------------------------------------------------------
// Shared with Regular Forms
//
// The mappings a Regular Form (Settings > Form Setup) can use come from the
// backend's form_setup/form_fields.py. The builder offers the same list, read
// from GET /inspection_form_builder/field-mappings/, so a field added there
// appears here with no frontend change.
//
// Such a mapping is stored as `form:` + the Regular Forms path, e.g.
// "form:Inspected inspections > Inspection Code". The browser cannot walk that
// path — it is a chain of Django relations — so the editor asks the server for
// the values (POST /inspections/{id}/form-field-values/) and keeps them on the
// inspection as `formFieldValues`, where resolveStaticValue reads them. The
// server's own renderer resolves them the same way, through the same code that
// prints a Regular Form, so both families print the same text.
// ---------------------------------------------------------------------------

const PATH_SEPARATOR = ' > ';

// The Regular Forms tree is rooted at the assigned inspector; the inspection
// itself hangs off this branch.
const INSPECTION_BRANCH = 'Inspected inspections';

// Settings > Global Variables: admin-configured values read off no record.
// Mirrors GLOBAL_VARIABLES_BRANCH in the backend's global_variables/constants.py.
const GLOBAL_VARIABLES_BRANCH = 'Global variables';

export interface FormFieldGroup {
  path: string[];
  fields: StaticFieldOption[];
}

const formFieldPath = (value: string): string[] =>
  value.slice(FORM_FIELD_PREFIX.length).split(PATH_SEPARATOR);

/**
 * Where a Regular Forms path reads from, in the builder's own words:
 * ['Inspected inspections', 'Permit'] -> 'Inspection › Permit',
 * ['Parent company'] -> 'Inspector › Parent company',
 * ['Global variables'] -> 'Global variables'.
 */
export const formFieldGroupLabel = (path: string[]): string => {
  if (path[0] === INSPECTION_BRANCH) {
    return ['Inspection', ...path.slice(1)].join(' › ');
  }
  // Not the inspector's: these belong to no record at all.
  if (path[0] === GLOBAL_VARIABLES_BRANCH) {
    return path.join(' › ');
  }
  return ['Inspector', ...path].join(' › ');
};

/** A `form:` mapping to a Settings > Global Variables entry. */
export const isGlobalVariableMapping = (value?: string): boolean =>
  isFormFieldMapping(value) && formFieldPath(value)[0] === GLOBAL_VARIABLES_BRANCH;

// A Global Variable's identity, as the server matches it: letters and digits
// only, lower-cased (global_variables/models.py normalize_name).
const normalizeVariableName = (name: string): string =>
  (name || '').toLowerCase().replace(/[^a-z0-9]/g, '');

/**
 * Do two mappings print the same thing? Exact match, except that Global
 * Variables are matched like the server matches them, so a variable re-created
 * as "qa permit fee" still serves a field mapped to "QA Permit Fee".
 */
export const isSameMapping = (a?: string, b?: string): boolean => {
  if (a === b) return true;
  if (!isGlobalVariableMapping(a) || !isGlobalVariableMapping(b)) return false;
  const nameA = formFieldPath(a).slice(1).join(PATH_SEPARATOR);
  const nameB = formFieldPath(b).slice(1).join(PATH_SEPARATOR);
  return normalizeVariableName(nameA) === normalizeVariableName(nameB);
};

/** 'Inspection › Inspection Code' for a `form:` value. */
export const formFieldLabel = (value: string): string => {
  const path = formFieldPath(value);
  return `${formFieldGroupLabel(path.slice(0, -1))} › ${path[path.length - 1]}`;
};

/** Every distinct `form:` mapping a form's static fields use. */
export const formFieldMappingsOf = (markup?: Record<string, any>): string[] => {
  const found = new Set<string>();
  Object.values(markup || {}).forEach(page => {
    if (!page || typeof page !== 'object') return;
    Object.values(page).forEach((item: any) => {
      if (item && item.isStatic && isFormFieldMapping(item.fieldName)) {
        found.add(item.fieldName);
      }
    });
  });
  return Array.from(found).sort();
};
